import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Observable,
  createHttpLink,
  NormalizedCacheObject
} from '@apollo/client';
import { RestLink } from 'apollo-link-rest';
import { getCsrfTokenLink } from '@/apollo/csrfTokenLink';
import getConfig from 'next/config';
import { fetchClientWithRetries } from '@tools/fetchClient';
import type { ConnectionParams } from 'subscriptions-transport-ws';
import { getSubscriptionToken } from '@/apollo/getSubscriptionToken';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { WebSocketLink } from './WebSocketLink';
import { onErrorLink } from './onErrorLink';

const LOG = LoggerFactory.create('apollo/links');

const { publicRuntimeConfig } = getConfig();

export const linkFetchAndRetryIfServerBusy = (url: string, options: RequestInit): Promise<Response> => {
  return fetchClientWithRetries.fetch(url, options);
};

export const videoMeetingLink = createHttpLink({
  uri: '/video-meeting/graphql',
  credentials: 'include'
});

export const httpLink = createHttpLink({
  uri: publicRuntimeConfig.GRAPH_URI,
  fetch: linkFetchAndRetryIfServerBusy,
  credentials: 'include'
});

// Setup rest links to endpoints not usable in the graphQL tier
export const restLink = new RestLink({
  uri: publicRuntimeConfig.BASE_PATH,
  bodySerializers: {
    fileEncode: (data, headers): RestLink.SerializedBody => {
      const formData = new FormData();
      formData.append('file', data, data.name);
      return { body: formData, headers };
    }
  }
});

/**
 * Configure Video Discussions Endpoint
 */
export const videoDiscussionLink = createHttpLink({
  uri: `${publicRuntimeConfig.BASE_PATH}/api/videoDiscussions`,
  fetch: linkFetchAndRetryIfServerBusy
});

/**
 * Configure SpeedNetworking Endpoint
 */
export const speedNetworkingLink = createHttpLink({
  uri: `${publicRuntimeConfig.BASE_PATH}/api/speed-networking`,
  fetch: linkFetchAndRetryIfServerBusy
});

/**
 * Terminating link that does nothing.
 */
export const terminateLink = new ApolloLink(operation => {
  LOG.debug('Terminate operation with no action', operation);
  return Observable.of();
});

/** For making one off requests that don't get saved to the shared Apollo cache */
function getApolloClient(attendeeId: string, terminatingLink: ApolloLink): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    link: ApolloLink.from([getCsrfTokenLink(attendeeId), onErrorLink, terminatingLink]),
    cache: new InMemoryCache()
  });
}

function createWebSocketLink(
  eventId: string,
  attendeeId: string,
  websocketUri: string,
  terminatingLink: ApolloLink
): WebSocketLink {
  return new WebSocketLink({
    uri: websocketUri,
    options: {
      reconnect: true,
      reconnectionAttempts: 10,
      inactivityTimeout: 86_400_000, // 24 hours
      connectionParams: async (): Promise<ConnectionParams> => {
        if (attendeeId) {
          const client = getApolloClient(attendeeId, terminatingLink);
          try {
            const token = await getSubscriptionToken(client);
            LOG.debug(`Generated oneTimeToken for Websocket connection to ${websocketUri}`);
            if (token) {
              return {
                oneTimeToken: token,
                eventId,
                attendeeId
              };
            }
          } catch (error) {
            LOG.error(`Failed to get subscription token for ${websocketUri}`, error);
            throw new Error('WebSocketLink connectionParams error', { cause: error });
          }
          throw new Error('WebSocketLink connectionParams error - no token');
        }
        throw new Error('attendeeId is required');
      }
    }
  });
}

export function createAttendeeHubWebSocketLink(
  eventId: string,
  attendeeId: string,
  websocketUri: string
): WebSocketLink {
  return createWebSocketLink(eventId, attendeeId, websocketUri, httpLink);
}

export function createVideoMeetingWebSocketLink(
  eventId: string,
  attendeeId: string,
  videoMeetingWebsocketUri: string
): WebSocketLink {
  return createWebSocketLink(eventId, attendeeId, videoMeetingWebsocketUri, videoMeetingLink);
}
